/**
 * Page glue for server mode (`?server=ws://host:port`): what main.ts calls so
 * its own `srv` branches stay one line each.
 *
 *   const srv = await connectServer(urlParams.get('server'), view);  // null offline
 *   if (srv) prepareServerSave(save, srv);
 *   net = srv ? installServerMode({...}) : installNet({...});
 *
 * The server owns the world, the inventory, stats, mobs and items; nothing of
 * the game is saved in the browser in server mode.
 */

import type * as THREE from 'three';
import type { ChunkManager } from '../world/manager';
import type { WorldSave } from '../world/save';
import type { DimensionId } from '../world/dimension';
import type { Player } from '../player/player';
import type { EntityHost } from '../entities/host';
import { effectiveLight } from '../entities/host';
import type { EnvUniforms } from '../render/env';
import type { MenuHost } from '../ui/menus';
import type { NetController } from '../net';
import { RemotePlayers } from '../net/remotePlayers';
import { clearSaveMemory, type SaveInternals } from '../net/room';
import { sanitizeName } from '../net/validate';
import { getChatOverlay } from '../ui/chat';
import { loadOrCreateKey, normalizeServerUrl } from './connection';
import { ServerSession, type ServerPlayerView, type WorldPort } from './serverSession';
import { describeDrop, describeRefusal, notice, refusalButtons, type ServerChoice } from './entry';

export { ServerSession } from './serverSession';

const NAME_KEY = 'opencraft.net.name'; // the name the Options screen edits (net/index.ts uses the same key)

function storedName(): string {
  try { const raw = (localStorage.getItem(NAME_KEY) ?? '').trim(); if (raw) return sanitizeName(raw); } catch { /* storage unavailable */ }
  return '';
}

/** Set by connectServer: this page plays on the build's own server (the notices offer "Back to title"), and Play was already asked for. */
let pageChoice: ServerChoice = { url: null, autoplay: false, configured: false };

/**
 * Log in to the game server this page plays on (`choice`, from
 * entry.ts chooseServer). Resolves once the server has welcomed us, or with
 * null when there is no server: the offline game. While the server cannot be
 * reached the notice says so and counts the attempts (connection.ts backs
 * off); a refused login stays on the notice with its reason and a way out.
 */
export async function connectServer(choice: ServerChoice | string | null, view: number): Promise<ServerSession | null> {
  const c: ServerChoice = typeof choice === 'string' || choice === null ? { url: choice, autoplay: false, configured: false } : choice;
  if (!c.url) return null;
  pageChoice = c;
  const url = normalizeServerUrl(c.url);
  const key = loadOrCreateKey();
  const name = storedName() || 'Player' + (parseInt(key.slice(0, 4), 16) % 10000).toString().padStart(4, '0');
  const srv = new ServerSession({ url, key, name, view });
  const box = notice();
  const back: [string, () => void][] = c.configured ? [['Back to title', () => { location.reload(); }]] : [];
  const slow = setTimeout(() => box.set('Connecting to the server...', back), 400);
  const offDrop = srv.onDrop((retryMs, reason) => { clearTimeout(slow); box.set(describeDrop(false, srv.drops, retryMs, reason) + (c.configured ? '' : `\n${url}`), back); });
  const off = srv.onStatus((s) => {
    if (s !== 'refused') return;
    clearTimeout(slow);
    const r = describeRefusal(srv.refusal);
    box.set(r.text, refusalButtons(r, c.configured));
  });
  srv.hold(); // main.ts calls live() when every subscriber exists
  srv.start();
  for (;;) {
    try { await srv.ready(); break; } catch { await new Promise<void>(() => { /* refused: stay on the notice */ }); }
  }
  clearTimeout(slow);
  off();
  offDrop();
  box.remove();
  return srv;
}

/**
 * The save in server mode: an empty object in memory that is never written.
 * World, inventory, stats, spawn point and game mode are the server's and
 * arrive with the login; the offline world's save is not touched.
 */
export function prepareServerSave(save: WorldSave, srv: ServerSession): void {
  const s = save as unknown as SaveInternals;
  clearSaveMemory(s); // drop what the offline load() put in memory
  s.dimension = srv.dim;
  save.gameMode = 'survival'; // until the server says otherwise (`stats`)
  save.weather = null;
  save.flush = () => { /* nothing of the game is kept in this browser */ };
}

export interface ServerModeDeps {
  srv: ServerSession;
  chunks: ChunkManager;
  player: Player;
  entityHost: EntityHost;
  sky: { time: number };
  scene: THREE.Scene;
  env: EnvUniforms;
  menus: MenuHost;
  canvas: HTMLElement;
  /** The server put us somewhere (login, correction, teleport): freeze there until the terrain is in. */
  spawnAt(x: number, y: number, z: number, yaw?: number, pitch?: number): void;
  /** Other players with what their entity record adds (client/entities.ts); default: the session's rows. */
  players?: { remotePlayers(out: ServerPlayerView[]): ServerPlayerView[] };
}

/**
 * Wire the session to the page: chunks and block changes into the
 * ChunkManager, time into the sky, other players into the renderer, chat and
 * the player list into the overlay. Returns the object main.ts keeps in `net`.
 */
export function installServerMode(d: ServerModeDeps): NetController {
  let strays = 0;
  const { srv, chunks } = d;
  const chat = getChatOverlay(document.body, () => 2);
  const remote = new RemotePlayers(d.scene, d.env, (x, y, z) => effectiveLight(d.entityHost, x, y, z));

  const world: WorldPort = {
    predict: (x, y, z, id) => chunks.predictBlock(x, y, z, id),
    revert: (cells) => chunks.revert(cells),
    applyServer: (x, y, z, id) => chunks.applyRemote(x, y, z, id),
    batch: (fn) => chunks.batchEdits(fn),
    chunk: (dim, cx, cz, blocks) => chunks.receiveChunk(dim, cx, cz, blocks),
    unload: (cx, cz) => chunks.unloadChunk(cx, cz),
    reset: (dim: DimensionId) => chunks.setDimension(dim),
  };

  // The first welcome arrived before the page existed (connectServer): main.ts
  // spawns from srv.welcome itself, and the chunks sent since then are handed
  // over by attach(). A later welcome (reconnect) resets the world and spawns
  // us where the server says.
  if (chunks.dimension !== srv.dim) chunks.setDimension(srv.dim);
  srv.attach(world);
  // A block edit made by a local rule the server has no intent for (see
  // docs/server-model-todo.md) is dropped: the world only changes through the
  // predicted actions of client/blocks.ts and what the server sends.
  chunks.sendEdit = (x, y, z, id) => {
    if (strays++ < 5) console.debug(`[srv] dropped a local block edit at ${x},${y},${z} -> ${id}`);
  };
  d.sky.time = srv.time;
  srv.on('welcome', (m) => {
    d.sky.time = m.time;
    d.spawnAt(m.x, m.y, m.z, m.yaw, m.pitch);
    chat.setStatus(null);
  });
  srv.on('time', (m) => { d.sky.time = m.time; });
  srv.on('pos', (m) => {
    if (m.dim !== chunks.dimension) chunks.setDimension(m.dim);
    d.spawnAt(m.x, m.y, m.z);
  });
  // `from` is the sender's name ('' = the server itself)
  srv.on('chat', (m) => chat.add(srv.names.get(m.from) ?? m.from ?? '', m.text, !m.from));
  srv.on('join', (m) => { if (m.id !== srv.id) chat.add('', `${m.name} joined the game`, true); });
  // 'leave' handlers run after the session forgot the name, so keep our own copy
  const seen = new Map<string, string>(srv.names);
  srv.on('welcome', (m) => { seen.clear(); for (const p of m.players) seen.set(p.id, p.name); });
  srv.on('join', (m) => { seen.set(m.id, m.name); });
  srv.on('leave', (m) => {
    const name = seen.get(m.id);
    if (name) chat.add('', `${name} left the game`, true);
    seen.delete(m.id);
  });
  // The connection states a player must be able to read (entry.ts has the wording): a dropped socket is retried
  // with backoff and counted in the status line; a refusal (full, old page, logged in elsewhere, kicked) ends the
  // session, so it covers the frozen world with its reason and a button.
  const box = notice();
  srv.onDrop((retryMs, reason) => { chat.setStatus(describeDrop(true, srv.drops, retryMs, reason)); });
  srv.onStatus((s) => {
    if (s === 'reconnecting') chat.setStatus('Connection lost. Reconnecting...');
    else if (s === 'refused') {
      const r = describeRefusal(srv.refusal);
      chat.setStatus(`Disconnected: ${srv.refusal || 'connection closed'}`);
      if (document.pointerLockElement) document.exitPointerLock();
      box.set(r.text, refusalButtons(r, pageChoice.configured));
    } else if (s === 'online') { chat.setStatus(null); box.remove(); }
  });

  // ---- name (Options screen) ----
  d.menus.playerName = srv.name;
  d.menus.onPlayerName = (n) => {
    if (!(n ?? '').trim()) return d.menus.playerName;
    const t = sanitizeName(n);
    try { localStorage.setItem(NAME_KEY, t); } catch { /* ignore */ }
    if (t !== srv.name) chat.add('', 'The new name is used the next time you connect.', true);
    return t;
  };

  // ---- keys: T or / opens chat, Tab lists players ----
  let tabHeld = false;
  let listSig = '';
  const refreshPlayers = (): void => {
    const want = tabHeld || (d.menus.open && d.menus.currentScreen === 'pause');
    if (!want) { if (listSig) { listSig = ''; chat.showPlayers(null); } return; }
    const rows = [...srv.names].map(([id, name]) => ({ name: name + (id === srv.id ? ' (you)' : ''), ping: 0, isHost: false }));
    const sig = rows.map((r) => r.name).join('|');
    if (sig === listSig) return;
    listSig = sig;
    chat.showPlayers(rows);
  };
  window.addEventListener('keydown', (e) => {
    if (chat.open) return;
    if (e.code === 'Tab') { e.preventDefault(); e.stopImmediatePropagation(); tabHeld = true; refreshPlayers(); return; }
    if (document.pointerLockElement !== d.canvas) return;
    if ((e.code === 'KeyT' || e.code === 'Slash') && !e.repeat) { e.preventDefault(); e.stopImmediatePropagation(); chat.show(); }
  }, true);
  window.addEventListener('keyup', (e) => { if (e.code === 'Tab') { tabHeld = false; refreshPlayers(); } }, true);

  // `?play=1` skips the title (tests, the agent API); main.ts sets onPlay after this call
  if (pageChoice.autoplay || new URLSearchParams(location.search).get('play') === '1') {
    const go = (): void => { if (d.menus.onPlay) d.menus.onPlay(null); else setTimeout(go, 50); };
    setTimeout(go, 0);
  }

  return {
    frame() {
      remote.update(d.players ?? srv);
      refreshPlayers();
      chat.update();
    },
    session: null,
    useOnMob: () => null,
  };
}
